import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, MapPin, Calendar, Users, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CandidaturePageQCM = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    activite: '',
    chiffre_affaires: '',
    objectif: '',
    engagement: ''
  });

  // Theme colors - VIVANT (men only)
  const accentColor = '#D4A574';
  const accentBg = 'bg-[#D4A574]';
  const accentText = 'text-[#D4A574]';
  const accentHover = 'hover:bg-[#C39563]';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelect = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const isStep1Valid = form.activite && form.chiffre_affaires;
  const isStep2Valid = form.objectif && form.engagement;
  const isStep3Valid = form.prenom && form.nom && form.email && form.telephone;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStep3Valid) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/candidatures`, {
        prenom: form.prenom,
        nom: form.nom,
        email: form.email,
        telephone: form.telephone,
        gender: 'homme',
        destination: 'ESSAOUIRA - Secret Camp',
        formule: 'VIVANT - 6 nuits - 6 500€',
        activite: form.activite,
        chiffre_affaires: form.chiffre_affaires,
        objectif: form.objectif,
        engagement: form.engagement
      });
      setIsComplete(true);
    } catch (err) {
      console.error('Submission error:', err);
      setError("Une erreur s'est produite. Merci de réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success Screen
  if (isComplete) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className={`w-20 h-20 ${accentBg} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-italiana text-3xl tracking-[0.15em] mb-3 text-white uppercase">
            Candidature reçue
          </h1>
          <p className="text-white/60 text-sm mb-2">
            Merci {form.prenom}.
          </p>
          <p className="text-white/40 text-xs mb-8">
            Ton profil va être étudié par notre équipe. Si ta candidature est retenue, tu recevras un lien pour réserver ton appel découverte.
          </p>
          <div className="bg-white/5 rounded-xl p-4 mb-8 border border-white/10">
            <p className="text-white/50 text-xs">
              Seuls les profils alignés avec l'esprit VIVANT seront contactés sous 48h.
            </p>
          </div>
          <Link 
            to="/" 
            className="block text-xs text-white/40 hover:text-white/60 transition-colors"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="pt-8 pb-6 px-4 text-center">
        <Link to="/" className="font-italiana text-xl tracking-[0.4em] text-white inline-block mb-6">
          <span style={{ color: accentColor }}>V</span>IVANT
        </Link>
        <h1 className="font-italiana text-2xl md:text-3xl tracking-[0.12em] text-white mb-2 uppercase">
          Candidature
        </h1>
        <p className="text-white/50 text-sm">Étape {step}/3 • Sélection sur dossier</p>
        
        {/* Progress Bar */}
        <div className="max-w-xs mx-auto mt-4 flex gap-2">
          {[1, 2, 3].map((s) => (
            <div 
              key={s} 
              className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-[#D4A574]' : 'bg-white/10'}`}
            />
          ))}
        </div>
      </div>

      {/* Form Container */}
      <div className="px-4 pb-10">
        <div className="max-w-lg mx-auto">
          
          {/* STEP 1: Qualification */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <p className={`${accentText} text-xs tracking-[0.2em] mb-2`}>PARLONS DE TOI</p>
                <h2 className="text-white text-xl font-italiana tracking-wider">Quel est ton profil ?</h2>
              </div>

              {/* Activité */}
              <div>
                <label className="block text-white/40 text-[10px] tracking-wider mb-3 uppercase">Ton activité</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    'Entrepreneur / CEO',
                    'Freelance / Consultant',
                    'Investisseur',
                    'Cadre dirigeant',
                    'Autre'
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleSelect('activite', option)}
                      className={`w-full p-4 rounded-xl border text-left text-sm transition-all ${
                        form.activite === option 
                          ? 'bg-[#D4A574]/20 border-[#D4A574] text-white' 
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chiffre d'affaires */}
              <div>
                <label className="block text-white/40 text-[10px] tracking-wider mb-3 uppercase">Chiffre d'affaires annuel</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    'Moins de 100K€',
                    '100K€ - 500K€',
                    '500K€ - 1M€',
                    '1M€ - 5M€',
                    'Plus de 5M€'
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleSelect('chiffre_affaires', option)}
                      className={`w-full p-4 rounded-xl border text-left text-sm transition-all ${
                        form.chiffre_affaires === option 
                          ? 'bg-[#D4A574]/20 border-[#D4A574] text-white' 
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                disabled={!isStep1Valid}
                onClick={() => setStep(2)}
                className={`w-full py-4 ${accentBg} ${accentHover} text-white text-sm tracking-[0.15em] rounded-full transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase mt-8`}
              >
                Continuer
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* STEP 2: Motivation */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <p className={`${accentText} text-xs tracking-[0.2em] mb-2`}>TES OBJECTIFS</p>
                <h2 className="text-white text-xl font-italiana tracking-wider">Pourquoi VIVANT ?</h2>
              </div>

              {/* Objectif */}
              <div>
                <label className="block text-white/40 text-[10px] tracking-wider mb-3 uppercase">Ton objectif principal</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    'Scaler mon business',
                    'Reprendre ma forme physique',
                    'Développer mon réseau',
                    'Trouver de la clarté',
                    'Tout ça à la fois'
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleSelect('objectif', option)}
                      className={`w-full p-4 rounded-xl border text-left text-sm transition-all ${
                        form.objectif === option 
                          ? 'bg-[#D4A574]/20 border-[#D4A574] text-white' 
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Engagement */}
              <div>
                <label className="block text-white/40 text-[10px] tracking-wider mb-3 uppercase">Es-tu prêt à t'engager ?</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    'Oui, je suis 100% engagé',
                    'J\'ai besoin de plus d\'infos',
                    'Je réfléchis encore'
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleSelect('engagement', option)}
                      className={`w-full p-4 rounded-xl border text-left text-sm transition-all ${
                        form.engagement === option 
                          ? 'bg-[#D4A574]/20 border-[#D4A574] text-white' 
                          : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-4 bg-white/5 text-white/60 text-sm tracking-[0.1em] rounded-full transition-all hover:bg-white/10"
                >
                  Retour
                </button>
                <button
                  type="button"
                  disabled={!isStep2Valid}
                  onClick={() => setStep(3)}
                  className={`flex-1 py-4 ${accentBg} ${accentHover} text-white text-sm tracking-[0.15em] rounded-full transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase`}
                >
                  Continuer
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Contact Info */}
          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-8">
                <p className={`${accentText} text-xs tracking-[0.2em] mb-2`}>DERNIÈRE ÉTAPE</p>
                <h2 className="text-white text-xl font-italiana tracking-wider">Tes coordonnées</h2>
              </div>

              {/* Experience Summary */}
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-lg font-italiana tracking-wider">ESSAOUIRA</p>
                    <p className="text-white/50 text-xs">Secret Camp • 6 nuits</p>
                  </div>
                  <div className="text-right">
                    <p className={`${accentText} text-xl font-italiana`}>6 500€</p>
                  </div>
                </div>
              </div>
            
              {/* Prénom & Nom */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/40 text-[10px] tracking-wider mb-2 uppercase">Prénom</label>
                  <input
                    type="text"
                    name="prenom"
                    value={form.prenom}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4A574] transition-colors"
                    placeholder="Ton prénom"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/40 text-[10px] tracking-wider mb-2 uppercase">Nom</label>
                  <input
                    type="text"
                    name="nom"
                    value={form.nom}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4A574] transition-colors"
                    placeholder="Ton nom"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-white/40 text-[10px] tracking-wider mb-2 uppercase">Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4A574] transition-colors"
                  placeholder="ton@email.com"
                  required
                />
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-white/40 text-[10px] tracking-wider mb-2 uppercase">Téléphone</label>
                <input
                  type="tel"
                  name="telephone"
                  value={form.telephone}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4A574] transition-colors"
                  placeholder="+33 6 00 00 00 00"
                  required
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-4 bg-white/5 text-white/60 text-sm tracking-[0.1em] rounded-full transition-all hover:bg-white/10"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={!isStep3Valid || isSubmitting}
                  className={`flex-1 py-4 ${accentBg} ${accentHover} text-white text-sm tracking-[0.15em] rounded-full transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    'Envoyer ma candidature'
                  )}
                </button>
              </div>

              <p className="text-white/30 text-[10px] text-center mt-4">
                Ta candidature sera étudiée sous 48h.
              </p>
            </form>
          )}

          {/* Back to home Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-white/40 hover:text-white/60 text-xs tracking-wider transition-colors">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidaturePageQCM;
